'use client';

import React from 'react';
import {
  FileText,
  Users,
  BarChart2,
  Wand2,
  CheckCircle2,
  LucideIcon,
  ArrowRight
} from 'lucide-react';
import { Button } from "@/components/ui/Button";
import Link from 'next/link';
import { usePageView } from '@/lib/hooks/usePageView';

interface Step {
  icon: LucideIcon;
  title: string;
  description: string;
  points: string[];
  gradient: string;
}

const steps: Step[] = [
  {
    icon: FileText,
    title: "Create Your Project",
    description: "Set up your matching project and customize questions to capture the criteria that matters most to your organization.",
    gradient: "from-amber-600 to-amber-700",
    points: [
      "Import or add participants manually",
      "Choose from 2,000+ pre-built questions",
      "Create custom questionnaires",
      "Set group size preferences",
      "Define matching constraints",
      "Configure project timeline"
    ]
  },
  {
    icon: Users,
    title: "Collect Responses",
    description: "Share your questionnaire with participants and gather their responses securely. No account creation required for participants.",
    gradient: "from-amber-700 to-amber-800",
    points: [
      "One-click participant onboarding",
      "End-to-end encrypted responses",
      "Real-time completion tracking",
      "Automated email reminders"
    ]
  },
  {
    icon: Wand2,
    title: "AI Processing",
    description: "Our advanced AI algorithm analyzes all participant responses to calculate compatibility scores and find optimal matches.",
    gradient: "from-amber-800 to-amber-900",
    points: [
      "Multi-dimensional analysis",
      "Intelligent pattern recognition",
      "Compatibility scoring system",
      "Constraint satisfaction"
    ]
  },
  {
    icon: BarChart2,
    title: "Generate & Compare",
    description: "Explore different grouping scenarios with various configurations. Compare results side-by-side to find the perfect arrangement.",
    gradient: "from-amber-600 to-amber-800",
    points: [
      "Multiple scenario testing",
      "Adjustable group sizes",
      "Side-by-side comparison",
      "Export to CSV, Excel, PDF"
    ]
  }
];

interface StepCardProps {
  step: Step;
  index: number;
}

const StepCard = ({ step, index }: StepCardProps) => {
  return (
    <div className="group relative bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 lg:p-8 hover:border-amber-600/20 transition-all duration-300">
      {/* Hover gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`} />
      
      <div className="relative flex flex-col md:flex-row items-start gap-6">
        {/* Icon and Number */}
        <div className="relative flex-shrink-0">
          <div className={`p-4 rounded-2xl bg-gradient-to-br ${step.gradient} shadow-xl`}>
            <step.icon className="h-8 w-8 text-white" />
          </div>
          <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-900 border-2 border-white/10 text-white flex items-center justify-center text-sm font-bold">
            {index + 1}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-amber-500 group-hover:to-amber-600 group-hover:bg-clip-text transition-all">
            {step.title}
          </h3>
          <p className="text-slate-400 mb-6 leading-relaxed">{step.description}</p>

          <div className="grid sm:grid-cols-2 gap-3">
            {step.points.map((point, pIndex) => (
              <div
                key={pIndex}
                className="flex items-center gap-2 text-sm"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                <span className="text-slate-300">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function LearnMore() {
  usePageView();
  
  return (
    <div className="container mx-auto px-4 py-16 md:py-24">
      {/* Hero Section */}
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <p className="text-amber-500 font-semibold text-sm uppercase tracking-wider mb-3">
          How It Works
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
          Four Simple Steps to
          <br className="hidden sm:block" />
          <span className="bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">
            {" "}Perfect Matches
          </span>
        </h1>
        <p className="text-lg text-slate-400">
          Our intelligent matching system uses advanced algorithms to create optimal groups. Get started in minutes, not hours.
        </p>
      </div>

      {/* Steps */}
      <div className="max-w-4xl mx-auto space-y-6">
        {steps.map((step, index) => (
          <StepCard
            key={index}
            step={step}
            index={index}
          />
        ))}
      </div>

      {/* CTA Section */}
      <div className="mt-20 max-w-4xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-700 via-amber-800 to-amber-900" />
          
          <div className="relative px-6 py-12 lg:py-16 lg:px-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Create Better Teams?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Join hundreds of organizations using Easy Pairing to create more effective and harmonious groups.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/auth/signup">
                <Button
                  size="lg"
                  className="bg-white text-amber-700 hover:bg-white/90 font-semibold px-8"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link href="/contact">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/20 text-white hover:bg-white/10 px-8"
                >
                  Schedule Demo
                </Button>
              </Link>
            </div>
            <p className="text-white/60 text-sm mt-6">No credit card required</p>
          </div>
        </div>
      </div>
    </div>
  );
}
